/**
 * Pure helpers for turning a Pelecard transaction into the card details a
 * receipt displays.
 *
 * These live here — not in the tour-registration service that first grew them —
 * because both receipt paths need the same mapping:
 *
 *   • tour registration (server): api/utils/tourRegistrationUtils/services/receipt.service.ts
 *   • the Accounting tab (browser): AccountingSection/GeneratePdfs/Receipt/*
 *
 * The two used to disagree, which is how the Accounting tab ended up printing
 * hardcoded placeholder card values. Keep this module free of imports that only
 * resolve on the server (db, logger, config) — it is bundled into the client.
 */

export type WalletType = 'GooglePay' | 'ApplePay' | null;

export interface InstallmentPlan {
    count: number;
    firstPaymentAmount: number;
    fixedPaymentAmount: number;
}

/**
 * The human-facing transaction reference for a Pelecard deal. Prefer Pelecard's
 * own TransactionPelecardId / TransactionId; fall back to a UserKey slice. This
 * is the value stored as the receipt's `reference` and the idempotency key that
 * ties a receipt to its Pelecard transaction.
 */
export const deriveTransactionRef = (dealDetails: any): string =>
    dealDetails?.TransactionPelecardId ||
    dealDetails?.TransactionId ||
    (dealDetails?.UserKey ? String(dealDetails.UserKey).slice(0, 32) : '');

export const detectWalletType = (dealDetails: any): WalletType => {
    if (dealDetails?.IsGooglePay || dealDetails?.GooglePay) return 'GooglePay';
    if (dealDetails?.IsApplePay || dealDetails?.ApplePay) return 'ApplePay';
    return null;
};

export const deriveCardBrand = (dealDetails: any): string => {
    // Pelecard exposes the brand under several field names. Critical detail:
    //   CreditCardBrand   = numeric code (1=Visa, 2=Mastercard, …)
    //   CardEnName        = English brand name (e.g. "Visa", "Mastercard")
    //   CardHebrewName    = Hebrew brand name
    // Always prefer the human-readable name fields; fall back to the code only
    // as a last resort so the receipt doesn't show a bare integer like "2".
    return (
        dealDetails?.CardEnName ||
        dealDetails?.CardHebrewName ||
        dealDetails?.CardName ||
        dealDetails?.CreditCardCompanyIssuer ||
        dealDetails?.CreditCardCompany ||
        dealDetails?.CCType ||
        (dealDetails?.CreditCardBrand ? String(dealDetails.CreditCardBrand) : '') ||
        'Credit Card'
    );
};

// Pelecard returns dates as "DD/MM/YYYY HH:mm:ss" (Israeli order). Convert to
// ISO so downstream date formatters don't mis-parse the month/day. Returns ''
// for null/unparseable so the receipt's Value Date column simply hides instead
// of showing "Invalid Date".
export const pelecardDateToISO = (raw: string | undefined | null): string => {
    if (!raw) return '';
    const match = String(raw).match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (!match) {
        // Already ISO or some other format — let downstream try.
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString();
    }
    const [, dd, mm, yyyy, hh = '00', mi = '00', ss = '00'] = match;
    const iso = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}.000Z`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

// Take a Pelecard-style masked PAN (`455744******4990` or `XXXXXXXXXXXX4990`)
// and turn it into the conventional grouped mask `**** **** **** 4990`. PCI
// allows displaying first-6 + last-4, but only the last-4 is universally safe
// for customer-facing receipts.
export const maskCardNumber = (raw: string | undefined | null): string => {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    const last4 = digits.slice(-4);
    if (!last4) return '';
    return `**** **** **** ${last4}`;
};

export const buildInstallmentPlan = (dealDetails: any): InstallmentPlan | null => {
    const count = Number(dealDetails?.TotalPayments) || 1;
    if (count <= 1) return null;

    const firstPaymentAgorot = Number(dealDetails?.FirstPaymentTotal) || 0;
    const fixedPaymentAgorot = Number(dealDetails?.FixedPaymentTotal) || 0;
    if (firstPaymentAgorot > 0 && fixedPaymentAgorot > 0) {
        return {
            count,
            firstPaymentAmount: firstPaymentAgorot / 100,
            fixedPaymentAmount: fixedPaymentAgorot / 100
        };
    }

    // Pelecard doesn't always populate FirstPaymentTotal/FixedPaymentTotal
    // even when TotalPayments correctly reports a real split (observed on a
    // credit ("קרדיט") deal) — dividing a missing field by 100 used to print
    // a literal "3 X 0" on the receipt, which reads as broken rather than as
    // a missing value. Fall back to an even split of the actually-charged
    // total (DebitTotal, agorot): exact for a plain equal-installment deal,
    // a reasonable approximation for an interest-adjusted credit deal —
    // rounded down per-installment with the first one absorbing the
    // remainder, so first + (count-1) × fixed always sums back to the real
    // total to the agora rather than drifting from it.
    const totalAgorot = Number(dealDetails?.DebitTotal) || 0;
    if (totalAgorot <= 0) return null;
    const fixedAgorot = Math.floor(totalAgorot / count);
    const firstAgorot = totalAgorot - fixedAgorot * (count - 1);
    return {
        count,
        firstPaymentAmount: firstAgorot / 100,
        fixedPaymentAmount: fixedAgorot / 100
    };
};

/**
 * Pelecard's `DebitCurrency` on a response: `'4'` means "this ILS
 * installment deal is dollar-linked" (עסקת תשלומים צמודה ל-$). The app's own
 * dollar-linked split charges through DebitByIntInEmv and presents its result
 * in this same shape (see pelecardUtils/linkedSplit.ts). Deliberately NOT added to
 * `PELECARD_CURRENCY_CODES` / `pelecardCurrencyToCode`: `4` is not a
 * currency identity the way ILS/USD/EUR are — `DebitTotal` etc. are STILL
 * reported in ILS agorot even when this is set, so treating `4` as "the
 * currency is USD" without also converting the amounts would show a shekel
 * figure mislabelled as dollars. See `dollarLinkedDealInUsd` below and
 * `generateAndSaveReceipt` (receipt.service.ts) for the conversion this drives.
 */
export const DOLLAR_LINKED_DEBIT_CURRENCY = '4';

export const isDollarLinkedDeal = (dealDetails: any): boolean =>
    String(dealDetails?.DebitCurrency ?? '') === DOLLAR_LINKED_DEBIT_CURRENCY;

/**
 * A dollar-linked split (pelecardUtils/linkedSplit.ts) as a DOLLAR receipt sees
 * it. The charge reports DebitTotal / FirstPaymentTotal / FixedPaymentTotal in
 * shekel agorot; this swaps them for US cents — the total is the frozen amount
 * the agent charged, split the way buildInstallmentPlan splits any total — and
 * returns the frozen rate the shekels were fixed at, which is the rate the
 * receipt records. Same conversion as the tour-registration receipt
 * (receipt.service.ts) for the app's own split, which always carries both
 * `TaytanDisplayAmount` and `TaytanFrozenRate` (normaliseLinkedSplitResult).
 * Unlike that receipt it does not fall back to today's rate when they are
 * missing — `null` then, as for any other deal. A staff screen given a
 * dollar-linked deal that maps to `null` must not issue the receipt.
 */
export const dollarLinkedDealInUsd = (dealDetails: any): { deal: any; rate: number } | null => {
    if (!isDollarLinkedDeal(dealDetails)) return null;
    const rate = Number(dealDetails?.TaytanFrozenRate);
    const totalCents = Math.round(Number(dealDetails?.TaytanDisplayAmount));
    if (!(rate > 0) || !(totalCents > 0)) return null;
    const count = Number(dealDetails?.TotalPayments) || 1;
    const fixedCents = count > 1 ? Math.floor(totalCents / count) : 0;
    return {
        rate,
        deal: {
            ...dealDetails,
            DebitTotal: totalCents,
            FirstPaymentTotal: count > 1 ? totalCents - fixedCents * (count - 1) : totalCents,
            FixedPaymentTotal: fixedCents
        }
    };
};

/**
 * The card fields a receipt stores on `info.paymentMethodObject`. These exact
 * key names are what pdfBuilders/buildReceipt.ts reads to fill the
 * "Credit Company / Card No. / Exp. Date / Reference" body box — a receipt that
 * stores them under any other name renders an empty box.
 */
export interface ReceiptCardDetails {
    creditCompanyName: string;
    cardNo: string;
    expDate: string;
    reference: string;
    /** ISO charge time — the F.O.P table's V.Date column. */
    valueDate: string;
    /** First installment when paying in instalments; null for a single charge. */
    firstPayment: number | null;
    /** The REMAINING instalments, e.g. "2 X 3566.68"; '' for a single charge. */
    additionalPayments: string;
    installmentPlan: InstallmentPlan | null;
    walletType: WalletType;
    /** Pelecard's CreditCardCompanyClearer — picks the receipt's clearing account. */
    creditCardCompanyClearer: string | null;
    /** Pelecard's CreditType: 8 instalments (split in the ledger), 6 credit (one line). */
    creditType: string | null;
}

/**
 * Map an approved Pelecard deal (`ResultData`) onto those keys.
 *
 * Installments follow the same convention as the tour-registration receipt:
 * Pelecard's TotalPayments counts the first payment, so the "additional"
 * count is one less — first + remaining × fixed adds back up to the total.
 */
export const buildReceiptCardDetails = (dealDetails: any): ReceiptCardDetails => {
    const installmentPlan = buildInstallmentPlan(dealDetails);
    const remainingCount = installmentPlan ? Math.max(installmentPlan.count - 1, 0) : 0;
    return {
        creditCompanyName: deriveCardBrand(dealDetails),
        cardNo: maskCardNumber(dealDetails?.CreditCardNumber),
        expDate: dealDetails?.CreditCardExpDate || '',
        reference: deriveTransactionRef(dealDetails),
        valueDate: pelecardDateToISO(dealDetails?.TransactionInitTime),
        firstPayment: installmentPlan ? installmentPlan.firstPaymentAmount : null,
        additionalPayments:
            installmentPlan && remainingCount > 0
                ? `${remainingCount} X ${installmentPlan.fixedPaymentAmount}`
                : '',
        installmentPlan,
        walletType: detectWalletType(dealDetails),
        creditCardCompanyClearer:
            dealDetails?.CreditCardCompanyClearer != null && String(dealDetails.CreditCardCompanyClearer).trim() !== ''
                ? String(dealDetails.CreditCardCompanyClearer)
                : null,
        creditType:
            dealDetails?.CreditType != null && String(dealDetails.CreditType).trim() !== ''
                ? String(dealDetails.CreditType)
                : null
    };
};
